<template>
    <div class="verify-page">
        <div class="verify-page__container">
            <logos-container class="verify-page__logos" />
        <h1 class="verify-page__title">{{ $t('Device Verification') }}</h1>
        <p class="verify-page__empty-message">
            <span class="fas fa-info-circle verify-page__icon-warning"></span>
                {{ $t('For security purposes, please verify your device. Check your email for a verification code. If not received, check spam folder. Contact support if you need help.') }}
        </p>
        <b-alert
            v-if="tooManyRequests"
            variant="danger"
            class="mt-3"
        >
            {{ $t('You have made too many requests. Please try again later.') }}
        </b-alert>
        <p class="verify-page__text">{{ $t('Enter your verification code to login') }}</p>
        <div>
            <form @submit.prevent="verify()">
                <div class="mb-3">
                    <ui-input
                        id="code"
                        name="code"
                        type="number"
                        placeholder="000000"
                        v-model="token"
                        :label="$t('Code')"
                        :field="v$.token"
                    />
                </div>
                <ui-button :loading="loading" class="w-100 mb-0" type="submit" variant="primary">
                    {{ $t('Validate code') }}
                </ui-button>
                <ui-button
                :disabled="timeRemaining > 0 || validatingCode || tooManyRequests || sendingCode"
                :loading="sendingCode && temporalNotificationType === notificationType"
                class="w-100 mb-2 mt-3"
                variant="secondary"
                @click="sendCodeRecaptcha(notificationType)"
                >
                    {{ $t('Resend code') }}
                </ui-button>
                <p v-if="timeRemaining > 0" class="verify-page__wait-message">
                    {{ $t('It is necessary to wait to resend the code {seconds} seconds', { seconds: timeRemaining }) }}
                </p>
                <div class="verify-page__other-methods">
                    <ui-button
                        v-if="notificationType !== notificationTypes.WHATSAPP"
                        class="verify-page__other-method-button"
                        variant="secondary"
                        :disabled="timeRemaining > 0 || validatingCode || tooManyRequests || sendingCode"
                        :loading="sendingCode && temporalNotificationType === notificationTypes.WHATSAPP"
                        @click="sendCodeRecaptcha(notificationTypes.WHATSAPP)"
                    >
                        <span v-if="!(sendingCode && temporalNotificationType === notificationTypes.WHATSAPP)" class="verify-page__other-method-icon fab fa-whatsapp"></span>
                        <span class="verify-page__other-method-button-text">
                            {{ $t('Get a code via WhatsApp') }}
                        </span>
                        <span class="verify-page__other-method-arrow fas fa-arrow-right"></span>
                    </ui-button>
                    <ui-button
                        v-if="notificationType !== notificationTypes.OMNI"
                        class="verify-page__other-method-button"
                        variant="secondary"
                        :disabled="timeRemaining > 0 || validatingCode || tooManyRequests || sendingCode"
                        :loading="sendingCode && temporalNotificationType === notificationTypes.OMNI"
                        @click="sendCodeRecaptcha(notificationTypes.OMNI)"
                    >
                        <span v-if="!(sendingCode && temporalNotificationType === notificationTypes.OMNI)" class="verify-page__other-method-icon fas fa-sms"></span>
                        <span class="verify-page__other-method-button-text">
                            {{ $t('Get a code via SMS') }}
                        </span>
                        <span class="verify-page__other-method-arrow fas fa-arrow-right"></span>
                    </ui-button>
                    <ui-button
                        v-if="notificationType !== notificationTypes.EMAIL"
                        class="verify-page__other-method-button"
                        variant="secondary"
                        :disabled="timeRemaining > 0 || validatingCode || tooManyRequests || sendingCode"
                        :loading="sendingCode && temporalNotificationType === notificationTypes.EMAIL"
                        @click="sendCodeRecaptcha(notificationTypes.EMAIL)"
                    >
                        <span v-if="!(sendingCode && temporalNotificationType === notificationTypes.EMAIL)" class="verify-page__other-method-icon fas fa-sms"></span>
                        <span class="verify-page__other-method-button-text">
                            {{ $t('Get a code via Email') }}
                        </span>
                        <span class="verify-page__other-method-arrow fas fa-arrow-right"></span>
                    </ui-button>
                </div>
            </form>
        </div>
        </div>
        <vue-recaptcha
        class="w-100"
        ref="recaptchaComponent"
        size="invisible"
        loadRecaptchaScript
        :sitekey="CONST.CAPTCHACODE"
        @verify="sendCode"
        />
    </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import VueRecaptcha from 'vue-recaptcha';
import { required, maxLength, minLength, helpers } from '@vuelidate/validators';

import UiInput from '@/components/ui/inputs/Input.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import LogosContainer from '@/components/ui/LogosContainer.vue';

import webViewMixin from '@/plugins/mixin/web-view.mixin';
import recaptchaUtils from '@/utils/recaptcha.utils';

import { OMNI, WHATSAPP, EMAIL } from '@/constants/notification-types.js';

export default {
    components: { UiInput, UiButton, LogosContainer, VueRecaptcha },
    mixins: [webViewMixin],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loading: false,
            sendingEmail: false,
            token: '',
            isCodeFromEmail: false,
            sendingCode: false,
            tooManyRequests: false,
            validatingCode: false,
            timeRemaining: 0,
            temporalNotificationType: '',
            notificationType: EMAIL,
            notificationTypes: {
                EMAIL,
                WHATSAPP,
                OMNI
            },
            user: null,
            loadingAccount: false,
            waitTime: 30,
            timeInterval: null,
        };
    },
    validations() {
        return {
            token: {
                required: helpers.withMessage(this.$t('validations.required', { field: this.$t('code') }), required),
                minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(6)),
                maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(6)),
                $autoDirty: true,
            },
        };
    },
    async mounted() {
        await this.getAccountDetails();
        this.$nextTick(() => {
            this.setTimeRemaining();
        });
    },
    beforeUnmount() {
        if (this.timeInterval) {
            clearInterval(this.timeInterval);
        }
    },
    methods: {
        async getAccountDetails() {
            try {
                this.loadingAccount = true;

                const { data } = await this.axiosAccount.get('/api/accounts/me');

                this.user = data;
            } catch(error) {
                this.handleErrors(error, 'accounts');
            } finally {
                this.loadingAccount = false;
            }
        },
        async verify() {
            try {
                this.loading = true;

                this.v$.$touch();

                if (this.v$.$invalid) {
                    return;
                }

                const token = `${this.user.email}:${this.token}`;
                const encodedToken = Buffer.from(token).toString('base64');
                const config = { headers: { 'Authorization': `Basic ${encodedToken}` } };

                const payload = {
                    token: ''
                };

                if (this.isCodeFromEmail) {
                    payload.type = 'email';
                }

                const { data } = await this.axiosAccount.post('/api/login/otp', payload, config);
                let { redirect_url } = data;
                const webview = this.$route.query.webview;

                if (webview) {
                    redirect_url = this.formatWebviewRedirectUrl(redirect_url);
                }

                window.location.replace(redirect_url);
            } catch(error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async sendCodeRecaptcha(notification_type) {
            this.temporalNotificationType = notification_type;

            if (!recaptchaUtils.isAvailable()) {
                this.sendCode('');
                return;
            }

            this.$refs.recaptchaComponent.reset();
            this.$refs.recaptchaComponent.execute();
        },
        async sendCode(token) {
            try {
                this.sendingCode = true;
                let payload = {
                    type: this.temporalNotificationType || this.notificationType,
                    action: this.temporalNotificationType === EMAIL ? 'email_device' : 'device_verification',
                    message: this.temporalNotificationType === EMAIL ? 'Security Alert: New Login Detected' : "",
                    from: 'Tendencys',
                    token,
                };

                await this.axiosAccount.post('/api/notifications/tfa', payload);
                this.setTimeRemaining();

                if (this.temporalNotificationType) {
                    this.notificationType = this.temporalNotificationType;
                    this.temporalNotificationType = null;
                }

            } catch(error) {
                this.handleErrors(error, 'notifications');
            } finally {
                this.sendingCode = false;

            }
        },
        handleErrors(error, resource) {
            if (error?.response?.status === 429 && resource === 'notifications') {
                this.tooManyRequests = true;
                return;
            }

            if (error?.response?.status === 404) {
                this.redirectToLogin();
                return;
            }

            if (error?.response?.status === 422 && resource === 'accounts') {
                this.$refs.registerPhoneModal.show();
                return;
            }

            this.showError(error);
        },
        setTimeRemaining() {
            if (this.timeInterval) {
                clearInterval(this.timeInterval);
            }
            
            this.timeRemaining = this.waitTime;
            this.timeInterval = setInterval(() => {
                if (this.timeRemaining > 0) {
                    this.timeRemaining--;
                } else {
                    clearInterval(this.timeInterval);
                    this.timeInterval = null;
                }
            }, 1000);
        },
    },
};
</script>

<style lang="scss">
.verify-page {
    align-items: center;

    &__container {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
    }

    &__logos {
        display: flex;
        justify-content: center;
    }
    &__title {
        font-size: 35px;
        font-weight: 600;
        text-align: center;
        margin: 20px;
    }

    &__text {
        color: $accounts-secondary;
        font-size: 16px;
        font-weight: 500;
        margin-top: 20px;
        text-align: left;
    }

    &__send-code {
        color: $accounts-secondary !important;
        font-weight: 500 !important;
        padding: 10px !important;
        text-align: center;
    }

    &__empty-message {
        background-color: #FFFFFF;
        border-left: 5px solid $general-warning;
        border-bottom: 1px solid #eff1f8;
        border-right: 1px solid #eff1f8;
        border-top: 1px solid #eff1f8;
        padding: 16px;
        text-align: left;
        width: 100%;
        margin-bottom: 0;
    }

    &__icon-warning {
        color: $general-warning !important;
        margin-right: 8px;
    }

     &__other-methods {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        justify-content: center;
        margin-top: 16px;
        margin-bottom: 32px;
        width: 100%;
    }

    &__wait-message {
        align-items: center;
        color: $general-black;
        font-size: 0.9rem;
        font-weight: 400;
        margin: 0 auto 15px auto;
        max-width: 400px;
        background-color: $general-white;
        border-radius: 8px;
        padding: 10px;
        text-align: center;

        strong {
            font-weight: 800;
        }
    }

}
</style>
